
import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Clock } from 'lucide-react';
import { getStatusColor } from "@/hooks/useNotifications";
import { useMarkNotificationRead, useDismissNotification } from "@/hooks/useNotificationMutations";

const NotificationCard = ({ notification }) => {
  const markRead = useMarkNotificationRead();
  const dismiss = useDismissNotification();

  const isRead = !!notification.read;

  const handleAction = (action) => {
    // You can customize logic for different actions (e.g., wish, review, etc)
    console.log(`Performing action: ${action} for notification ${notification.id}`);
    // Optionally, trigger relevant backend logic or page navigation here
  };

  const handleMarkRead = () => {
    if (!isRead) markRead.mutate(notification.id);
  };

  const handleDismiss = () => {
    dismiss.mutate(notification.id);
  };

  const icon = notification.metadata?.icon ?? "🔔";
  const actions = notification.metadata?.actions ?? [];

  // Format timestamp
  const formatTimestamp = (iso: string) => {
    if (!iso) return "";
    const date = new Date(iso);
    return date.toLocaleString(undefined, {
      hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric'
    });
  };

  // Prevent undefined status error
  const displayStatus = typeof notification.status === 'string' && notification.status.length > 0
    ? notification.status.charAt(0).toUpperCase() + notification.status.slice(1)
    : 'Default';

  const statusColor = getStatusColor(
    typeof notification.status === 'string' && notification.status.length > 0
      ? notification.status
      : 'default'
  );

  return (
    <div
      className={`bg-white rounded-lg border transition-all duration-200 hover:shadow-md ${
        isRead ? 'border-gray-200' : 'border-purple-200 shadow-sm'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start space-x-3">
          <div className="text-2xl flex-shrink-0 mt-1" title="icon">
            {icon}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">
                  {notification.title}
                </h3>
                {!!notification.message && (
                  <p className="text-sm text-gray-600 mt-1">
                    {notification.message}
                  </p>
                )}
              </div>
              <div className="flex items-center space-x-2 ml-4">
                {!isRead && (
                  <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0" />
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDismiss}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  disabled={dismiss.isPending}
                  aria-label="Dismiss notification"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
            <div className="flex items-center space-x-2 mb-3">
              <Badge className={`text-xs ${statusColor}`}>
                {displayStatus}
              </Badge>
              <div className="flex items-center text-xs text-gray-500">
                <Clock className="w-3 h-3 mr-1" />
                {formatTimestamp(notification.triggered_on)}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {actions.map((action, idx) => (
                <Button
                  key={idx}
                  variant={action.variant}
                  size="sm"
                  onClick={() => handleAction(action.action)}
                  className="text-xs"
                >
                  {action.label}
                </Button>
              ))}
              {!isRead && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkRead}
                  // className="text-xs text-gray-500 hover:text-gray-700"
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded px-3 py-1 text-sm font-medium ml-auto"
                  disabled={markRead.isPending}
                >
                  Mark as Read
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCard;
